import { defineStore } from 'pinia';
import type {
    IMachineFilters,
    IMachineFiltersValues,
    IMachineGridDataItem,
    IMachineGridDataMeta,
    IMachineGridHeaders,
    IMachineStore,
} from '../../../shared/interfaces/machine-store.interface';
import {
    cloneDeep,
    concat,
    filter,
    find,
    findIndex,
    flatten,
    forEach,
    get,
    includes,
    keys,
    map,
    omit,
    set,
    toString,
    uniq,
} from 'lodash';
import type { IMachine } from '../../../shared/interfaces/machine.interface';
import { APP_MACHINE_STEPS, DISABLED_PROPERTIES } from '../../../config/form.config';
import type { IFormStep } from '../../../shared/interfaces/form-step.interface';
import type { IMachineProperty } from '../../../shared/interfaces/machine-property.interface';
import type { IFormValue } from '../../../shared/interfaces/form-value.interface';
import type { IFormValueCommand } from '../../../shared/interfaces/form-value-command.interface';
import { APP_MACHINES_CORE_STEPS } from '../../../config/app.config';
import type { IStepItem } from '../../../shared/forms/step-navigation/step-item.interface';
import type { IFormField } from '../../../shared/forms/form-field.interface';
import type { IApiErrorResponse } from '@/shared/services/api-client.service';

const initialStateMachinesStore: IMachineStore = {
    loading: true,
    filters: {
        model: [],
        tractor_loader_only: false,
        min_base_weight: null,
    },
    filtersValues: {
        name: '',
        model: null,
        tractor_loader_only: null,
        min_base_weight: null,
    },
    girdData: {
        headers: [
            // { title: 'search_grid_title_id', key: 'id', sortable: false },
            { title: 'search_grid_title_name', key: 'name', sortable: false },
            { title: 'search_grid_title_model', key: 'model', sortable: false },
            {
                title: 'search_grid_title_engine_hp',
                key: 'engineHp',
                sortable: false,
            },
            {
                title: 'search_grid_title_pto_hp',
                key: 'ptoHp',
                sortable: false,
            },
            {
                title: 'search_grid_title_base_weight',
                key: 'base_weight',
                sortable: false,
            },
            {
                title: 'search_grid_title_loader',
                key: 'loader',
                sortable: false,
            },
            {
                title: 'search_grid_title_loader_style',
                key: 'loader_style',
                sortable: false,
            },
            {
                title: 'search_grid_title_mount_3pt_type',
                key: 'mount_3pt_type',
                sortable: false,
            },
            {
                title: 'search_grid_title_max_lift_capacity',
                key: 'max_lift_capacity',
                sortable: false,
            },
            {
                title: 'search_grid_title_drawbar_max_load',
                key: 'drawbar_max_load',
                sortable: false,
            },
            {
                title: 'search_grid_title_max_pressure',
                key: 'front_pressure',
                sortable: false,
            },
            {
                title: 'search_grid_title_front_flow_rate',
                key: 'front_flow_rate',
                sortable: false,
            },

            //drawbar.max_load
        ],
        items: [],
        meta: {
            current_page: 0,
            items_per_page: 0,
            total_items: 0,
            total_pages: 0,
        },
    },
    currentMachine: {
        loading: false,
        machine: {
            machine: {
                id: '',
                name: '',
                comment: '',
                description: '',
                created_at: null,
                last_modified_at: null,
                specs: [],
            },
            compatible_implements: [],
            maybe_compatible_implements: [],
        },
        currentStep: null,
        steps: APP_MACHINE_STEPS,
        currentStepIntex: '',
        models: [],
        values: {},
        updateValues: {},
        propertyItmes: [],
        propertyCategories: [],
        implementsStep: '',
        implementsStepIndex: 0,
    },
    current: {
        laoding: false,
        properties: [],
        steps: [],
        currentStep: '',
        values: {},
        updateValues: {},
        confirmValues: {},
        disabledProperties: DISABLED_PROPERTIES,
        errors: null,
    },
};

const useMachinesStore = defineStore('machines', {
    state: () => ({ ...cloneDeep(initialStateMachinesStore) }),
    getters: {
        isCurrentPropertieDiasbled: (state) => {
            return (name: string): boolean => {
                return !!find(state.current.disabledProperties, (x) => x === name);
            };
        },
        selectMachineLoading: (state): boolean => state.loading,
        selectMachineFilters: (state): IMachineFilters => state.filters,
        selectMachineFiltersValues: (state): IMachineFiltersValues => state.filtersValues,
        selectMachineGridDataHeaders: (state): IMachineGridHeaders[] => state.girdData.headers,
        selectMachineGridDataItems: (state): IMachineGridDataItem[] => state.girdData.items,
        selectMachineGridDataMeta: (state): IMachineGridDataMeta => state.girdData.meta,
        selectCurrentMachine: (state): IMachine => state.currentMachine.machine,
        selectCurrentMachineLoading: (state): boolean => state.currentMachine.loading,
        selectCurrentMachineSteps: (state): IFormStep[] => state.currentMachine.steps,
        selectCurrentMachineCurrentStep: (state): IFormStep | null => state.currentMachine.currentStep,
        selectCurrentMachineCurrentStepIndex: (state): string => state.current.currentStep,
        selectCurrentMachineModels: (state): string[] => state.currentMachine.models,
        selectCurrentMachineValues: (state): IFormValue => state.current.values,
        selectCurrentConfirmMachineValues: (state): IFormValue => state.current.confirmValues,
        selectCurrentMachineUpdatedValues: (state): IFormValue => state.currentMachine.updateValues,
        selectCurrentMachinePropertyCategories: (state): string[] => {
            return state.currentMachine.propertyCategories;
        },
        selectCurrentMachinePropertyItems: (state): IMachineProperty[] => {
            return state.currentMachine.propertyItmes;
        },
        selectImplementsStep: (state): string => {
            return state.currentMachine.implementsStep;
        },
        selectImplementsStepIndex: (state): number => {
            return state.currentMachine.implementsStepIndex;
        },
        selectCurrentMachineErrors: (state): IApiErrorResponse | null => {
            return state.current.errors;
        },
    },
    actions: {
        // current
        setCurrentMachineErrors(errors: IApiErrorResponse | null) {
            this.current.errors = errors;
        },
        setCurrentLoading(loading: boolean) {
            this.current.laoding = loading;
        },
        setCurrentProperties(properties: IFormField[]) {
            this.current.properties = properties;
        },
        setCurrentDisabledProperties(disabledProperties: string[]) {
            this.current.disabledProperties = disabledProperties;
        },
        addCurrentDisabledProperties(disabledProperties: string[]) {
            const clone = cloneDeep(this.current.disabledProperties);
            const result = uniq([...clone, ...disabledProperties]);
            this.current.disabledProperties = result;
        },
        removeCurrentDisabledProperties(disabledProperties: string[]) {
            const clone = cloneDeep(this.current.disabledProperties);
            const result = filter(clone, (x) => !includes(disabledProperties, x));
            this.current.disabledProperties = result;
        },
        setCurrentSteps(steps: IStepItem[]) {
            this.current.steps = steps;
        },
        setCurrentStep(step: string) {
            this.current.currentStep = step;
        },

        updateCurrentStep(step: string, props: { key: string; value: any }[]) {
            const steps: IStepItem[] = cloneDeep(this.current.steps);
            const stepToUpdate: number = findIndex(steps, (s: IStepItem) => s.name === step);
            if (stepToUpdate !== -1) {
                forEach(props, (prop) => {
                    set(steps[stepToUpdate], prop.key, prop.value);
                });
            }
            this.current.steps = steps;
        },
        setCurrentValues(values: Record<string, unknown>) {
            const cleaned = omit(this.current.values, keys(values));
            const clone = cloneDeep(cleaned);
            this.current.values = Object.assign({}, clone, values);
        },
        setCurrentConfirmValues(values: Record<string, unknown>) {
            this.current.confirmValues = values;
        },
        resetCurrentValues() {
            this.current.values = {};
        },
        setCurrentUpdateValues(values: Record<string, unknown>) {
            this.current.updateValues = values;
        },
        resetCurrentUpdateValues() {
            this.current.updateValues = {};
        },
        // current
        setImplementsStep(implementsStep: string) {
            this.currentMachine.implementsStep = implementsStep;
        },
        setImplementsStepIndex(implementsStepIndex: number) {
            this.currentMachine.implementsStep = toString(
                get(
                    filter(
                        this.currentMachine.propertyCategories,
                        (x: string) => !includes(APP_MACHINES_CORE_STEPS, x)
                    ),
                    implementsStepIndex
                )
            );
            this.currentMachine.implementsStepIndex = implementsStepIndex;
        },
        setMachineLoading(loading: boolean) {
            this.loading = loading;
        },
        setMachineFilters(filters: IMachineFilters) {
            this.filters = filters;
        },
        setMachineFiltersValues(filtersValues: IMachineFiltersValues) {
            this.filtersValues = filtersValues;
        },
        resetMachineFiltersValues() {
            this.filtersValues = {
                name: '',
                model: ['All'],
                tractor_loader_only: null,
                min_base_weight: null,
            };
        },
        setMachineFiltersValue(key: string, value: string) {
            const filtersValues: IMachineFiltersValues = cloneDeep(this.filtersValues);
            set(filtersValues, key, value);
            this.filtersValues = filtersValues;
        },
        setMachineGridDataItems(items: IMachineGridDataItem[]) {
            this.girdData.items = items;
        },
        setMachineGridDataMeta(meta: IMachineGridDataMeta) {
            this.girdData.meta = meta;
        },
        deleteMachine(id: string) {
            const items: IMachineGridDataItem[] = filter(
                cloneDeep(this.girdData.items),
                (x: IMachineGridDataItem) => x.id !== id
            );

            this.girdData.items = items;
        },
        setMachineData(machine: IMachine) {
            this.currentMachine.machine = machine;
        },
        resetMachineData() {
            this.currentMachine.machine = initialStateMachinesStore.currentMachine.machine;
        },
        setCurrentMachineCurrentSteps(steps: IFormStep[]) {
            this.currentMachine.steps = steps;
        },
        setCurrentMachineCurrentStep(step: IFormStep | null) {
            this.currentMachine.currentStep = step;
            this.currentMachine.currentStepIntex = toString(get(step, 'name'));
        },
        setCurrentMachineCurrentStepIndex(index: string) {
            this.currentMachine.currentStepIntex = index;
        },
        setCurrentMachineModels(models: string[]) {
            this.currentMachine.models = models;
        },
        resetCurrentMachineModels() {
            this.currentMachine.models = initialStateMachinesStore.currentMachine.models;
        },
        setCurrentMachineLoading(loading: boolean) {
            this.currentMachine.loading = loading;
        },
        removeCurrentMachineCurrentValue(key: string) {
            const cloneValues = cloneDeep(this.currentMachine.values);
            forEach(cloneValues, (value, k) => {
                if (k === key) {
                    delete cloneValues[k];
                }
            });
            this.currentMachine.values = cloneValues;
        },
        removeCurrentMachineCurrentValues() {
            const cloneValues = cloneDeep(this.currentMachine.values);
            const correctvaluesKeys: string[] = map(
                flatten(map(flatten(map(cloneDeep(this.currentMachine.steps), (x) => concat(x.form))), (y) => y.items)),
                (z) => z.refFieldId
            );
            forEach(cloneValues, (value, k) => {
                if (!includes(correctvaluesKeys, k)) {
                    delete cloneValues[k];
                }
            });
            this.currentMachine.values = cloneValues;
        },
        updateCurrentMachineValues(data: IFormValueCommand) {
            this.currentMachine.values[data.key] = {
                value: data.value,
                type: data.type,
                item: data.item,
            };
        },
        resetCurrentMachineValues() {
            this.currentMachine.values = {};
            this.currentMachine.steps = APP_MACHINE_STEPS;
        },
        //
        updateCurrentMachineUpdatedValues(data: IFormValueCommand) {
            this.currentMachine.updateValues[data.key] = {
                value: data.value,
                type: data.type,
                item: data.item,
            };
        },
        resetCurrentMachineUpdatedValues() {
            this.currentMachine.updateValues = {};
        },
        //
        setcurrentMachinePropertyCategories(categories: string[]) {
            return (this.currentMachine.propertyCategories = categories);
        },
        resetcurrentMachinePropertyCategories() {
            return (this.currentMachine.propertyCategories =
                initialStateMachinesStore.currentMachine.propertyCategories);
        },
        setcurrentMachinePropertyItmes(items: IMachineProperty[]) {
            return (this.currentMachine.propertyItmes = items);
        },
        resetcurrentMachinePropertyItmes() {
            return (this.currentMachine.propertyItmes = initialStateMachinesStore.currentMachine.propertyItmes);
        },
        insertModel(model: string) {
            this.filters.model.push(model);
            this.currentMachine.models.push(model);
        },
    },
});

export default useMachinesStore;
